import axios from "axios"
import Cookies from "js-cookie"

import { NEW_APPLICATION_ID } from "../utils/constants"
import { installDpop } from "../core/infrastructure/security/dpop/axios-dpop"
import { getVerificationToken } from "../core/infrastructure/security/deviceVerification"

// FOC-478 — Ruta del endpoint de login tal como la ve el navegador.
// Debe coincidir con el pathname que el gateway lee para la auth pública.
// installDpop lo usa para omitir Authorization y ath en /authentication
// (en el login todavía no hay token que firmar).
const LOGIN_PATH = "/api/user/authentication"

// Token en memoria del modulo. Lo setea applyAuthData justo tras el login
// OK (via setSessionToken que exportamos abajo) y lo leen las queries
// subsiguientes. Es la fuente mas rapida y confiable: sincronico, sin
// depender de que Cookies.set / document.cookie reflejen antes del
// primer render. Se limpia en logout / sessionError. Un hard refresh lo
// pierde (ok: ahi caemos a cookie/localStorage para rehidratar).
let inMemorySessionToken = null
export const setSessionToken = (token) => {
  inMemorySessionToken = token || null
}
export const clearSessionToken = () => {
  inMemorySessionToken = null
}

// FOC-478 — Lee el token de sesión guardado en cookie.
// FOC-492 — Si no hay token de sesión, cae al token de verificación de
// dispositivo (purpose=device_verification) para que /device/verify y
// /device/resend se firmen con la misma instancia axios.
//
// Fallback a localStorage antes del verificationToken: tras un login OK,
// `applyAuthData` setea cookie `user-tk` Y localStorage.userInfoQursiva en
// la misma función. Los MUCHOS useEffects de la home (useGetAll — dos
// idénticos — useMyLearning, useCompletedCourses, useCertifiedCourses,
// validateAccess) disparan en paralelo apenas userInfoQursiva cambia; si
// alguna de esas lecturas de Cookies.get("user-tk") devolviera undefined
// por un microtick, el request caería al verificationToken (purpose=
// device_verification), el gateway lo rechazaría como TOKEN.PURPOSE_MISMATCH
// (401 TOKEN_INVALID) y onSessionError sacaría al usuario que acaba de
// loguearse. Leer el token del localStorage cubre ese race sin exponer
// el verificationToken fuera de su flow.
const getUserToken = () => {
  if (inMemorySessionToken) {
    console.log("[academy-auth] getUserToken: hit memory", inMemorySessionToken.slice(0, 20) + "...")

    return inMemorySessionToken
  }

  try {
    const stored = window.localStorage.getItem("userInfoQursiva")
    if (stored) {
      const parsed = JSON.parse(stored)
      if (parsed && parsed.tokenQursiva) {
        console.log("[academy-auth] getUserToken: hit localStorage")
        inMemorySessionToken = parsed.tokenQursiva

        return parsed.tokenQursiva
      }
    }
  } catch {
    /* localStorage bloqueado o JSON corrupto */
  }

  const raw = Cookies.get("user-tk")
  if (raw && raw !== "undefined") {
    console.log("[academy-auth] getUserToken: hit cookie")
    inMemorySessionToken = raw

    return raw
  }
  if (raw === "undefined") Cookies.remove("user-tk")

  const verif = getVerificationToken()
  console.warn("[academy-auth] getUserToken: NO TOKEN", {
    memory: !!inMemorySessionToken,
    localStorage: !!window.localStorage.getItem("userInfoQursiva"),
    cookie: !!Cookies.get("user-tk"),
    cookieRaw: document.cookie,
    verification: !!verif,
  })

  return verif
}

// FOC-488 — Al llegar un 401 con código de sesión (TOKEN_INVALID,
// DPOP_PROOF_INVALID, DEVICE_MISMATCH, APPLICATION_MISMATCH, SESSION_REVOKED)
// limpiamos residuos y llevamos al usuario a la home. La home monta
// `ModalHome` cuando no hay sesión ni código IBO, así el usuario vuelve a
// loguearse sin salir del flujo. El motivo se guarda en sessionStorage
// (one-shot) para que la pantalla destino muestre un toast descriptivo.
//
// Antes el redirect iba a /login?motivo=X — con el rediseño el login vive
// dentro del modal, no en una página aparte; mantener /login significaba
// obligar al usuario a cambiar de ruta solo para reloguearse.
//
// Fix loop (crítico): el flag `redirecting` en el módulo NO sobrevive a
// location.reload(); el bundle se re-ejecuta con el flag en false. Si la
// home dispara queries autenticadas aunque no haya sesión (useEffects que
// no gatean en userInfoQursiva), el 401 vuelve, llamamos onSessionError
// otra vez, recargamos otra vez → bucle.
//
// Doble guard:
//   1. Si al momento del 401 ya NO hay sesión local (user-tk + userInfo),
//      el 401 es esperado para una ruta auth llamada sin credenciales —
//      no es "sesión caída". Ignoramos sin navegar.
//   2. Flag `RECENT_REDIRECT_KEY` en sessionStorage con timestamp.
//      Sobrevive al reload. Durante los 10s posteriores a un redirect
//      ignoramos nuevos `onSessionError` (cubre carrera entre reload y
//      queries que ya salieron con el token viejo).
export const SESSION_MOTIVO_KEY = "academy-session-motivo"
const RECENT_REDIRECT_KEY = "academy-session-redirect-at"
const RECENT_REDIRECT_WINDOW_MS = 10 * 1000

// Un 401 aislado tras login OK suele ser un race del cliente (una query
// que salio con token mal armado por el motivo que sea) y NO una sesion
// caida real. Si fuera caida de sesion, TODAS las queries fallarian.
// Esperamos a ver al menos 2 fallos en una ventana corta antes de
// desloguear al usuario.
const UNAUTHORIZED_WINDOW_MS = 5 * 1000
const UNAUTHORIZED_THRESHOLD = 2
const recentUnauthorized = []

const hadLiveSession = () => {
  try {
    if (window.localStorage.getItem("userInfoQursiva")) return true
  } catch {
    /* storage bloqueado */
  }
  const raw = Cookies.get("user-tk")

  return !!(raw && raw !== "undefined")
}

const recentlyRedirected = () => {
  try {
    const at = Number(window.sessionStorage.getItem(RECENT_REDIRECT_KEY) ?? 0)

    return at > 0 && Date.now() - at < RECENT_REDIRECT_WINDOW_MS
  } catch {
    return false
  }
}

const shouldTreatAsSessionDown = () => {
  const now = Date.now()
  // Limpiar entradas fuera de la ventana
  while (recentUnauthorized.length && now - recentUnauthorized[0] > UNAUTHORIZED_WINDOW_MS) {
    recentUnauthorized.shift()
  }
  recentUnauthorized.push(now)

  return recentUnauthorized.length >= UNAUTHORIZED_THRESHOLD
}

let redirecting = false
const onSessionError = (code) => {
  if (typeof window === "undefined") return
  if (redirecting) return
  if (!hadLiveSession()) return
  if (recentlyRedirected()) return
  if (!shouldTreatAsSessionDown()) {
    // 401 aislado: lo registramos en consola pero no desloguamos.
    // Si vienen mas 401 pronto, el proximo cruzara el umbral.
    console.warn("[academy] onSessionError ignored (isolated 401):", code)

    return
  }
  redirecting = true

  clearSessionToken()
  try {
    window.localStorage.removeItem("userInfoQursiva")
  } catch {
    /* storage bloqueado */
  }
  Cookies.remove("user-tk")

  try {
    window.sessionStorage.setItem(SESSION_MOTIVO_KEY, code)
    window.sessionStorage.setItem(RECENT_REDIRECT_KEY, String(Date.now()))
  } catch {
    /* storage bloqueado — igual seguimos con el redirect */
  }

  // Si ya estamos en la home, un reload basta: ModalHome vuelve a montarse
  // porque userInfoQursiva ya no está. En otra ruta volvemos a `/` para
  // forzar ese mismo flujo (el usuario pierde la ruta de origen, pero sin
  // sesión no podía quedarse ahí de todas formas).
  const isHome = window.location.pathname === "/" || window.location.pathname === ""
  if (isHome) {
    window.location.reload()
  } else {
    window.location.assign("/")
  }
}

export const API = () => {
  const deviceId = typeof window !== "undefined" ? localStorage.getItem("__mmapiwsid") : null

  const headers = {
    "accept-language": "ES",
    "application-id": NEW_APPLICATION_ID
  }
  if (deviceId) {
    // Señal opcional para antifraude. No se usa para auth: el gateway
    // deriva device-id del cnf.jkt del DPoP, no de este header.
    headers["device-id"] = deviceId
  }

  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers
  })

  installDpop(instance, {
    loginPath: LOGIN_PATH,
    getToken: getUserToken,
    onSessionError
  })

  return instance
}
